// @flow
import type { Reducer } from 'redux';
import { createReducer } from 'redux-act';
import { createSelector } from 'reselect';

import { selectFromConfig } from '@gandi/redux-config';
import { createResource, type Resource } from '@gandi/redux-resource';

import type {
  ComponentName,
  ComponentStatus,
  Dispatch,
  FetchResponse,
  FullState,
  ResourceThunkAction,
  StatusPageResponse,
  StatusState,
} from '../types';

const UNKNOWN = 'UNKNOWN';
const EMPTY_OBJECT: { [component: ComponentName]: ComponentStatus } = {};

const sortOrder = {
  operational: 0,
  under_maintenance: 1,
  degraded_performance: 2,
  partial_outage: 3,
  major_outage: 4,
};

export const selectStatusUrl: any = selectFromConfig(
  'app.layout.status',
  'https://status.gandi.net'
);

// ********** RESOURCE **********
export const statusResource: Resource<> = createResource('status');

// ********** ACTIONS **********

export const fetchStatus =
  (): ((dispatch: Dispatch, getState: () => FullState) => any) =>
  (dispatch: Dispatch, getState: () => FullState) => {
    const statusUrl = selectStatusUrl(getState());
    const apiUrl = `${statusUrl}/api/v2/summary.json`;
    return dispatch(statusResource.fetchOne(apiUrl));
  };

// ********** SELECTORS **********
function getStatus(
  value: StatusState['value']
): 'success' | 'info' | 'warning' | 'danger' | void {
  switch (value) {
    case 'none':
    case 'operational':
      return 'success';
    case 'maintenance':
      return 'info';
    case 'minor':
      return 'warning';
    case 'major':
    case 'critical':
      return 'danger';

    default:
      return undefined;
  }
}

function getComponent(subject: ?string): ComponentName[] {
  const impactAll = ['APIs', 'Network', 'Portal'];

  switch (subject) {
    case 'domain':
    case 'marketplace':
      return [
        'DNS',
        'Domain name registration',
        'Web redirection services',
        ...impactAll,
      ];
    case 'hosting':
      return ['IAAS', ...impactAll];
    case 'shs':
      return ['PAAS', ...impactAll];
    case 'gandimail':
      return ['Gandimail', ...impactAll];
    case 'certificate':
      return ['SSL Certificates', ...impactAll];

    case 'migration':
    case 'feedback':
    case 'billing':
    case 'api':
    default:
      return [...impactAll];
  }
}

export const selectStatus: (
  state: FullState
) => 'success' | 'info' | 'warning' | 'danger' | void = createSelector(
  (state: FullState) => state.status,
  (state: StatusState) => getStatus(state.value)
);

export const selectServiceStatus: (
  state: FullState,
  props: string | void
) => ComponentStatus | void = createSelector(
  [
    (state: FullState) => state.status,
    (state: FullState, subject: string | void) => subject,
  ],
  (state: StatusState, subject: string | void): ComponentStatus | void => {
    if (!subject) {
      return undefined;
    }

    const components = getComponent(subject);

    // Actually, components is now sorted too, but rename var for clarity:
    const sorted = components.sort(
      (a, b) =>
        sortOrder[state.components[b]] - sortOrder[state.components[a]] ||
        sortOrder[state.components[b]] ||
        0 - sortOrder[state.components[a]] ||
        0
    );
    return sorted.length ? state.components[sorted[0]] : undefined;
  }
);

// ********** HANDLERS **********
const hostingIaaSMatch = /^Cloud/;
const hostingPaaSMatch = /^Simple Hosting/;
const hostingVPSMatch = /VPS/; // Check if VPS exist somewhere inside a string
export const fetchOneHandler = (
  state: StatusState,
  { response }: {| response: FetchResponse<200, StatusPageResponse> |}
): StatusState => ({
  ...state,
  value: response.data.status.indicator,
  components:
    response.data && response.data.components
      ? response.data.components.reduce((byName, service) => {
          // Keep top-level components
          if (!service.group_id)
            return { ...byName, [service.name]: service.status };

          // Split Hosting into PAAS and IAAS components
          if (hostingIaaSMatch.test(service.name))
            return { ...byName, IAAS: service.status };

          if (hostingPaaSMatch.test(service.name))
            return { ...byName, PAAS: service.status };

          if (hostingVPSMatch.test(service.name))
            return { ...byName, VPS: service.status };

          // Drop other sub-components
          return byName;
        }, EMPTY_OBJECT)
      : state.components,
});

// ********** REDUCER **********
export const initialState: StatusState = {
  value: UNKNOWN,
  components: EMPTY_OBJECT,
};

// $FlowFixMe[incompatible-exact]
const reducer: Reducer<StatusState, ResourceThunkAction> = createReducer(
  {
    [statusResource.fetchOne.success.getType()]: fetchOneHandler,
  },
  initialState
);

export default reducer;
